import { Separator } from '@/shared/components/ui/separator';
import { Skeleton } from '@/shared/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table';
import { cn } from '@/util/cn';

interface DataTableSkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  columnCount: number;
  rowCount?: number;
  filterableColumnCount?: number;
  avancedFilters?: boolean;
  dateFilters?: boolean;
  customToolbar?: boolean;
  showViewOptions?: boolean;
  cellWidths?: string[];
  withPagination?: boolean;
  shrinkZero?: boolean;
}

export function DataTableSkeleton(props: DataTableSkeletonProps) {
  const {
    columnCount,
    rowCount = 20,
    filterableColumnCount = 0,
    avancedFilters = true,
    dateFilters = true,
    customToolbar = true,
    showViewOptions = true,
    cellWidths = ['auto'],
    withPagination = true,
    shrinkZero = false,
    className,
    ...skeletonProps
  } = props;

  return (
    <div className={cn('$tw-w-full $tw-overflow-auto', className)} {...skeletonProps}>
      <div className="$tw-flex $tw-w-full $tw-flex-col $tw-justify-between $tw-gap-6 $tw-overflow-auto $tw-rounded-t-md $tw-bg-background-2 $tw-p-6">
        <div className="$tw-flex $tw-w-full $tw-items-start $tw-justify-between">
          <div className="$tw-flex $tw-items-center $tw-gap-2">
            {filterableColumnCount > 0
              ? Array.from({ length: filterableColumnCount }).map((_, i) => (
                  <Skeleton key={i} className="$tw-h-10 $tw-w-48 $tw-border-dashed" />
                ))
              : null}
          </div>

          {dateFilters ? (
            <div className="$tw-flex $tw-flex-col $tw-gap-2">
              <div className="$tw-flex $tw-items-center $tw-gap-2">
                <Skeleton className="$tw-h-10 $tw-w-52 $tw-border-dashed" />
                <Skeleton className="$tw-h-10 $tw-w-52 $tw-border-dashed" />
              </div>

              <Skeleton className="$tw-h-4 $tw-w-96 $tw-border-dashed" />
            </div>
          ) : null}
        </div>

        <div
          className={cn(
            '$tw-flex $tw-items-center',
            avancedFilters ? '$tw-justify-between' : '$tw-justify-end'
          )}
        >
          {avancedFilters ? <Skeleton className="$tw-h-10 $tw-w-48 $tw-border-dashed" /> : null}

          <div className="$tw-flex $tw-items-center $tw-gap-2">
            {customToolbar ? <Skeleton className="$tw-h-10 $tw-w-40 $tw-border-dashed" /> : null}
            {showViewOptions ? (
              <Skeleton className="$tw-ml-auto $tw-hidden $tw-h-10 $tw-w-40 lg:$tw-flex" />
            ) : null}
          </div>
        </div>
      </div>

      <Table>
        <TableHeader>
          {Array.from({ length: 1 }).map((_, i) => (
            <TableRow key={i} className="hover:$tw-bg-transparent">
              {Array.from({ length: columnCount }).map((_, j) => (
                <TableHead
                  key={j}
                  style={{
                    width: cellWidths[j],
                    minWidth: shrinkZero ? cellWidths[j] : 'auto',
                  }}
                  className="$tw-bg-table"
                >
                  <Skeleton className="$tw-h-6 $tw-w-full" />
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {Array.from({ length: rowCount }).map((_, i) => (
            <TableRow key={i} className="hover:$tw-bg-transparent">
              {Array.from({ length: columnCount }).map((_, j) => (
                <TableCell
                  key={j}
                  style={{
                    width: cellWidths[j],
                    minWidth: shrinkZero ? cellWidths[j] : 'auto',
                  }}
                >
                  <Skeleton className="$tw-h-6 $tw-w-full" />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {withPagination ? (
        <div className="$tw-flex $tw-w-full $tw-flex-col-reverse $tw-items-center $tw-justify-end $tw-gap-4 $tw-overflow-auto $tw-rounded-b-md $tw-bg-background $tw-p-1 $tw-py-3 $tw-pr-20 sm:$tw-flex-row sm:$tw-gap-8">
          <div className="$tw-flex $tw-flex-col-reverse $tw-items-center $tw-gap-4 $tw-text-sm sm:$tw-flex-row sm:$tw-gap-6 lg:$tw-gap-8">
            <div className="$tw-flex $tw-items-center $tw-space-x-2">
              <Skeleton className="$tw-h-8 $tw-w-24" />
              <Skeleton className="$tw-h-8 $tw-w-[4.5rem]" />
            </div>

            <div className="$tw-flex $tw-items-center $tw-justify-center $tw-text-sm $tw-font-medium">
              <Skeleton className="$tw-h-8 $tw-w-[4.5rem]" />
            </div>

            <Separator orientation="vertical" className="$tw-h-6" />

            <div className="$tw-flex $tw-items-center $tw-justify-center $tw-text-sm $tw-font-medium">
              <Skeleton className="$tw-h-8 $tw-w-[4.5rem]" />
            </div>

            <div className="$tw-flex $tw-items-center $tw-space-x-2">
              <Skeleton className="$tw-hidden $tw-size-8 lg:$tw-block" />
              <Skeleton className="$tw-size-8" />
              <Skeleton className="$tw-size-8" />
              <Skeleton className="$tw-hidden $tw-size-8 lg:$tw-block" />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
